<template>
  <div class="my-schedule">
    <v-sheet outlined class="d-flex align-center ma-2 px-4" height="60">
      <font class="font-weight-bold text-h6 grey--text text--darken-2">나의 예약 스케줄</font>
    </v-sheet>

    <v-sheet class="ma-2 pa-4" outlined>
      <div class="d-flex flex-wrap justify-space-between align-center mb-4">
        <DateSelector :color="$vuetify.theme.currentTheme.primary" :width="330" class="mr-4" :dateInit="date" @dateUpdated="handleDateChange" />
        <div class="font-weight-bold">선택 날짜: {{ formattedDate }} / 총 {{ reservations.length }}건</div>
      </div>

      <v-row no-gutters class="my-schedule__content">
        <v-col cols="12" md="5" class="pr-md-4 mb-6 mb-md-0">
          <v-sheet outlined class="rounded-xl">
            <div class="d-flex align-center justify-space-between px-4 py-3">
              <span class="font-weight-medium">예약 목록</span>
              <v-progress-circular v-if="loading" indeterminate color="primary" size="24" />
            </div>
            <v-divider />
            <v-list dense class="py-0">
              <template v-if="sortedReservations.length">
                <v-list-item
                  v-for="reservation in sortedReservations"
                  :key="reservation.id"
                  @click="selectReservation(reservation)"
                  class="my-schedule__list-item"
                  :class="{ 'my-schedule__list-item--active': selectedReservation && selectedReservation.id === reservation.id }"
                >
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium"> {{ reservation.time }} · {{ reservation.concept || "-" }} </v-list-item-title>
                    <v-list-item-subtitle> {{ reservation.name || reservation.user_id }} / {{ getTicketName(reservation) }} </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip x-small color="primary" text-color="white">{{ reservation.status }}</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </template>
              <div v-else class="py-8 text-center grey--text text--darken-1">
                <span v-if="!loading">선택한 날짜에 예약이 없습니다.</span>
              </div>
            </v-list>
          </v-sheet>
        </v-col>
        <v-col cols="12" md="7">
          <v-sheet outlined class="rounded-xl pa-6" height="100%">
            <p class="mb-4 font-weight-bold text-h6">예약 상세</p>
            <v-alert v-if="!selectedReservation && !loading && reservations.length === 0" type="info" outlined> 선택한 날짜에 예약이 없습니다. </v-alert>
            <v-alert v-else-if="!selectedReservation" type="info" outlined> 왼쪽 목록에서 예약을 선택해주세요. </v-alert>

            <template v-if="selectedReservation">
              <table class="my-schedule__detail-table">
                <tr>
                  <td>예약자명</td>
                  <td>{{ selectedReservation.name || "-" }}</td>
                </tr>
                <tr>
                  <td>전화번호</td>
                  <td>{{ selectedReservation.phone || "-" }}</td>
                </tr>
                <tr>
                  <td>이메일</td>
                  <td>{{ selectedReservation.email || "-" }}</td>
                </tr>
                <tr>
                  <td>상품타입</td>
                  <td>{{ getTicketName(selectedReservation) }}</td>
                </tr>
                <tr v-if="getOptionTitles(selectedReservation).length">
                  <td>옵션상품</td>
                  <td>{{ getOptionTitles(selectedReservation).join(" + ") }}</td>
                </tr>
                <tr>
                  <td>컨셉</td>
                  <td>{{ selectedReservation.concept || "-" }}</td>
                </tr>
                <tr>
                  <td>이용일</td>
                  <td>{{ formatReservationDate(selectedReservation) }}</td>
                </tr>
                <tr>
                  <td>작가</td>
                  <td>
                    <template v-if="selectedReservation.maker">
                      {{ selectedReservation.maker }}
                      <template v-if="selectedReservation.staffUserId">({{ selectedReservation.staffUserId }})</template>
                    </template>
                    <template v-else>-</template>
                  </td>
                </tr>
                <tr>
                  <td>상태</td>
                  <td>{{ selectedReservation.status }}</td>
                </tr>
                <tr>
                  <td>결제금액</td>
                  <td>{{ $toComma(selectedReservation.pay_amount || 0) }}원</td>
                </tr>
                <tr>
                  <td>결제일</td>
                  <td>{{ selectedReservation.created ? new Date(selectedReservation.created).toLocaleString() : "-" }}</td>
                </tr>
                <tr>
                  <td>주문번호</td>
                  <td>{{ selectedReservation.order_id || "-" }}</td>
                </tr>
                <tr v-if="selectedReservation.promotionCode">
                  <td>프로모션</td>
                  <td>{{ selectedReservation.promotionCode }}</td>
                </tr>
                <tr>
                  <td>상품 정보</td>
                  <td>
                    <code class="json-block">{{ selectedReservation.optionType || "-" }}</code>
                  </td>
                </tr>
                <tr>
                  <td>추가 옵션</td>
                  <td>
                    <code class="json-block">{{ selectedReservation.options || "-" }}</code>
                  </td>
                </tr>
                <tr>
                  <td>메모</td>
                  <td>{{ selectedReservation.memo || "-" }}</td>
                </tr>
              </table>
            </template>
          </v-sheet>
        </v-col>
      </v-row>
    </v-sheet>
  </div>
</template>

<script>
import DateSelector from "@/components/DateSelector"
import { mapGetters, mapState } from "vuex"

export default {
  components: {
    DateSelector,
  },
  data: () => ({
    date: null,
    reservations: [],
    selectedReservation: null,
    loading: false,
    promotions: [],
  }),
  computed: {
    ...mapState(["lafia_user"]),
    ...mapGetters(["userType"]),
    currentStaffUserId() {
      return this.lafia_user?.id || null
    },
    formattedDate() {
      return this.date ? this.date.format("YYYY.MM.DD") : "-"
    },
    sortedReservations() {
      return [...this.reservations].sort((a, b) => a.time.localeCompare(b.time))
    },
  },
  watch: {
    date: {
      handler() {
        if (!this.date) return
        this.loadMyReservations()
      },
      immediate: true,
    },
  },
  async mounted() {
    this.promotions = await this.getAllPromotions()
    if (!this.date) {
      this.date = this.dayjs().startOf("day")
    }
  },
  methods: {
    handleDateChange(date) {
      this.date = date.startOf("day")
    },
    async loadMyReservations() {
      if (!this.currentStaffUserId) {
        this.reservations = []
        this.selectedReservation = null
        return
      }

      this.loading = true
      try {
        const { data } = await this.$http.post("/api/reservation/select/specific/day/category/all", {
          params: {
            category: "라피아캐슬",
            year: this.date.year(),
            month: this.date.month() + 1,
            day: this.date.date(),
            staffUserId: this.currentStaffUserId,
          },
        })
        this.reservations = Array.isArray(data) ? data : []
        if (!this.reservations.length) {
          this.selectedReservation = null
        } else if (!this.selectedReservation || !this.reservations.find((item) => item.id === this.selectedReservation.id)) {
          this.selectedReservation = this.reservations[0]
        }
      } catch (error) {
        console.error("나의 예약 스케줄 불러오기 실패", error)
        this.reservations = []
        this.selectedReservation = null
      } finally {
        this.loading = false
      }
    },
    selectReservation(reservation) {
      this.selectedReservation = reservation
    },
    formatReservationDate(reservation) {
      if (!reservation) return "-"
      return `${reservation.year}.${reservation.month}.${reservation.day} / ${reservation.time}`
    },
    getTicketName(item) {
      const promotionCode = item.promotionCode && item.promotionCode.trim() !== "" ? item.promotionCode : "default"
      const promotion = this.promotions.find((p) => p.code.toLowerCase() === promotionCode.toLowerCase())
      if (promotion && promotion.products) {
        try {
          const products = JSON.parse(promotion.products)
          const product = products.find((p) => p.id === item.optionType)
          if (product && product.title?.kr) {
            let title = product.title.kr
            if (product.type === "group") {
              title = `${title}(인원 ${item.cloth}명)`
            }
            return title
          }
        } catch (e) {
          // ignore parsing error
        }
      }
      let productName = "이용권"
      switch (item.optionType) {
        case "option1":
          productName = "솔로샷"
          break
        case "option2":
          productName = "솔로샷 듀얼"
          break
        case "option3":
          productName = "그룹샷 " + item.cloth + "명"
          break
        case "option4":
          productName = "2인 할인 팩"
          break
        case "option5":
          productName = "3인 할인 팩"
          break
        case "option6":
          productName = "4인 할인 팩"
          break
      }
      return productName
    },
    getOptionTitles(reservation) {
      if (!reservation.options || reservation.options.trim() === "") {
        return []
      }

      try {
        const options = JSON.parse(reservation.options)
        return options
          .map((option) => {
            if (!option || !option.title) return ""

            let title = option.title

            if (typeof title === "string") {
              try {
                title = JSON.parse(title)
              } catch {
                return title
              }
            }

            if (typeof title === "object") {
              return title.kr || ""
            }

            return ""
          })
          .filter((title) => title)
      } catch (error) {
        console.error("옵션 파싱 실패", error)
        return []
      }
    },
    async getAllPromotions() {
      try {
        const response = await this.$http.post("/api/promotion/select/getAllProductsInfo")
        return response.data.map((promotion) => ({
          name: promotion.name,
          code: promotion.code,
          products: promotion.products,
          createdAt: promotion.created || null,
          isIndividual: promotion.isIndividual,
        }))
      } catch (e) {
        console.error("프로모션 정보를 가져오지 못했습니다.", e)
        return []
      }
    },
  },
  created() {
    if (!this.date) {
      this.date = this.dayjs().startOf("day")
    }
  },
}
</script>

<style scoped>
.my-schedule__content {
  min-height: 520px;
}

.my-schedule__list-item {
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
}

.my-schedule__list-item--active {
  background-color: rgba(239, 127, 171, 0.15);
}

.my-schedule__detail-table {
  width: 100%;
  border-collapse: collapse;
}

.my-schedule__detail-table tr td:first-child {
  width: 120px;
  font-weight: 600;
  color: #666;
  padding: 6px 12px;
  white-space: nowrap;
}

.my-schedule__detail-table tr td:last-child {
  padding: 6px 12px;
}

.json-block {
  display: block;
  white-space: pre-wrap;
  font-size: 12px;
  background: #f7f7f7;
  border-radius: 8px;
  padding: 8px;
  word-break: break-all;
}
</style>
